/* eslint-disable @typescript-eslint/default-param-last */
import { useEffect, useState, useCallback } from 'react';
import API_BASE_URL from 'src/constants';
import { FileObject } from 'src/interfaces/shared';
import i18n from 'src/localization/i18n';
import { toast } from 'react-toastify';
import { clearCachedImage } from 'src/components/ui/Image/Image';
import { ChatItem } from './chat';

export interface Product {
  id: number;
  active: boolean;
  article_number: string;
  barcode: string;
  name: string;
  weight: number;
  height: number;
  length: number;
  width: number;
  is_non_shippable: boolean;
  is_product_set: boolean;
  standard_price: number;
  list_price: number;
  currency_name: string;
  netto_weight: number;
  volume: number;
  hs_code: string;
  country_of_origin: string;
  chat: ChatItem[];
  hazardous_material: any;
  attachments: FileObject[];
  bom_components: BomComponent[];
  tags: ProductTag[];
  product_packagings: ProductPackaging[];
  product_status: ProductState;
  // to be checked
  type: string;
  use_expiration_date: boolean;
  removal_time: number;
  tracking: string;
  quality_check_enabled?: boolean;
  quality_checks?: QualityCheck[];
}

export interface QualityCheck {
  id: number;
  last_quality_check_date: string | null;
  product_id: number;
  quality_check_enabled: boolean;
  quality_check_instruction: string;
  quality_check_interval: string | null;
  quality_check_name: string;
}

export interface ProductPackaging {
  id: number;
  barcode: string;
  name: string;
  quantity: number;
}
export interface ProductTag {
  id: number;
  color: number;
  display_name: string;
}

export interface BomLine {
  id: number;
  product_id: number;
  quantity: number;
  uom: string;
  article_number: string;
  barcode: string;
  name: string;
}

export interface BomLineIn {
  product_id: number;
  quantity: number;
}

export interface BomComponent {
  id: number;
  product_id: number;
  type: string;
  quantity: number;
  uom: string;
  bom_lines: BomLine[];
}

interface ProductResponse {
  total: number;
  page: number;
  size: number;
  pages: number;
  items: Product[];
}

export enum ProductState {
  'DRAFT' = 'Draft',
  'CONFIRMED' = 'Confirmed',
  'TO_CHECK' = 'To Check',
}

export const typeOptions = [
  {
    value: 'Consumable',
    label: 'Consumable',
  },
  {
    value: 'Service',
    label: 'Service',
  },
  {
    value: 'Product',
    label: 'Product',
  },
];

// GET - PRODUCTS
export const fetchProducts = async (
  page = 1,
  size = 20,
  domain?: { key: string; value: string; operator: string }[],
): Promise<Product[]> => {
  try {
    const domainFilters = domain?.map(({ key, value, operator }) => `("${key}", "${operator}", "${value}")`).join(',');

    const url = new URL(`${API_BASE_URL}portal/products`);
    url.searchParams.append('page', page.toString());
    url.searchParams.append('size', size.toString());
    if (domainFilters) {
      url.searchParams.append('domain', `[${domainFilters}]`);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to GET products');
    }

    const data: Product[] = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

// GET - PRODUCT TAGS
export const fetchProductTags = async (): Promise<ProductTag[] | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}portal/products/tags/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch product tags');
    }

    const data: ProductTag[] = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching product tags:', error);
    return [];
  }
};

// GET - PRODUCT BY ID
export const fetchProductById = async (id: number): Promise<Product | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}portal/products/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch product');
    }

    const data: Product = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching product:', error);
    return null;
  }
};

// GET - PRODUCT IMAGE
export const getProductImageUrl = (id: number): string => {
  return `${API_BASE_URL}portal/products/${id}/image`;
};

// POST - UPDATE PRODUCT IMAGE
export const updateProductImage = async (id: number, image: File): Promise<boolean> => {
  try {
    const formData = new FormData();
    formData.append('image', image);

    const response = await fetch(`${API_BASE_URL}portal/products/${id}/image`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to update product image');
    }

    const imageUrl = getProductImageUrl(id);
    clearCachedImage(imageUrl);

    return true;
  } catch (error) {
    console.error('Error updating product image:', error);
    return false;
  }
};

// UPDATE - PRODUCT BY ID
export const updateProductById = async (id: number, data: Product): Promise<Product | undefined> => {
  try {
    if (!id) {
      throw new Error('Product ID is missing');
    }

    const accessToken = localStorage.getItem('access_token');

    // chat, type and attachments are not needed in the body and should be removed
    const { chat, type, attachments, ...productData } = data;

    // filter out attachments that are not yet created
    const newCreatedAttachments = (attachments || []).filter(attachment => !attachment.id);

    const finalBody = {
      ...productData,
      attachments: newCreatedAttachments || [],
    };

    const response = await fetch(`${API_BASE_URL}portal/products/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(finalBody),
    });

    if (!response.ok) {
      throw new Error('Failed to update product');
    }

    const updatedProduct: Product = await response.json();
    return updatedProduct;
  } catch (error) {
    console.error('Error updating product:', error);
    return undefined;
  }
};

// POST - CREATE PRODUCT
export const createNewProduct = async (product: Product, image?: File): Promise<Product | null> => {
  try {
    const accessToken = localStorage.getItem('access_token');

    const productData = { ...product, currency_name: 'EUR' } as Product;

    //  const requiredKeys = ['name'];

    const response = await fetch(`${API_BASE_URL}portal/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      throw new Error('Failed to create product');
    }

    const data = await response.json();

    if (data.id) {
      if (image) {
        await updateProductImage(data.id, image);
      }
      return data;
    }
    return null;
  } catch (error) {
    console.error('Error creating product:', error);
    return null;
  }
};

export const useProducts = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductResponse>({ items: [], total: 0, page: 1, size: 20, pages: 0 });

  const getProducts = useCallback(
    async (page: number = 1, size: number = 20, domain?: { key: string; value: string; operator: string }[]) => {
      try {
        setLoading(true);
        const data = await fetchProducts(page, size, domain);
        setProducts({
          items: data,
          total: data.length,
          page,
          size,
          pages: Math.ceil(data.length / size),
        });
      } catch (err) {
        setError('Failed to fetch products');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  return { products, loading, error, getProducts };
};

export const useProductTags = () => {
  const [tags, setTags] = useState<ProductTag[] | null>(null);

  const getTags = async () => {
    const tags = await fetchProductTags();
    setTags(tags);
  };

  useEffect(() => {
    getTags();
  }, []);
  return { tags };
};

export interface UseProductResult {
  product: Product | null;
  productImage: string | null;
  loading: boolean;
  error: string | null;
  getProductImage: (id: number) => Promise<void>;
  changeProductImage: (File: File | null, customId?: number) => Promise<void>;
  getProductById: (id: number) => Promise<void>;
  updateProduct: (product: Product) => Promise<boolean>;
  createProduct: (product: Product, image?: File) => Promise<Product | null>;
}
export const useProduct = (id?: number): UseProductResult => {
  const accessToken = localStorage.getItem('access_token');
  const [product, setProduct] = useState<Product | null>(null);
  const [productImage, setProductImage] = useState<string | null>(id ? getProductImageUrl(id) : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getProductById = useCallback(
    async (id: number) => {
      if (!id) return;
      setLoading(true);
      try {
        const product = await fetchProductById(id);
        if (product) {
          setProduct(product);
        }
      } catch (err) {
        console.error('Failed to fetch product');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  const getProductImage = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const imageUrl = getProductImageUrl(id);
        if (imageUrl) {
          setProductImage(imageUrl);
        } else {
          throw new Error('Failed to fetch product image');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to fetch product image');
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  const changeProductImage = useCallback(
    async (imageFile: File | null, customId?: number) => {
      if (!imageFile) return;
      setLoading(true);
      try {
        const success = await updateProductImage(customId || id!, imageFile);
        if (!success) {
          throw new Error('Failed to update product image');
        }
        if (product?.id) {
          await getProductImage(product.id);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to update product image');
      } finally {
        setLoading(false);
      }
    },
    [id, product?.id, getProductImage],
  );

  const createProduct = useCallback(
    async (product: Product, image?: File): Promise<Product | null> => {
      setLoading(true);
      try {
        const created = await createNewProduct(product, image);
        const success = Boolean(created);
        if (success) {
          toast.success(i18n.t('success.create_product'));
          return created;
        }
        throw new Error('Failed to create product');
      } catch (err) {
        toast.error(i18n.t('errors.create_product'));
        setError('Failed to create product');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  const updateProduct = useCallback(
    async (product: Product): Promise<boolean> => {
      if (!product.id) return false;
      setLoading(true);
      try {
        const updatedProduct = await updateProductById(product.id, product);
        if (!updatedProduct) {
          throw new Error('Failed to update product');
        }
        toast.success(i18n.t('success.update_product'));
        setProduct(updatedProduct);
        return true;
      } catch (err) {
        toast.error(i18n.t('errors.update_product'));
        setError('Failed to update product');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    if (id) {
      getProductById(id);
      getProductImage(id);
    }
  }, [id, getProductImage, getProductById]);

  return {
    productImage,
    loading,
    error,
    getProductImage,
    product,
    getProductById,
    createProduct,
    changeProductImage,
    updateProduct,
  };
};

export const validateCreateProduct = (product: Product): boolean => {
  const requiredKeys = ['name', 'width', 'height', 'length', 'weight'];

  // check if all required keys in product have a value and are not empty
  if (!requiredKeys.every(key => product[key as keyof Product])) {
    return false;
  }

  if (!!product.netto_weight && product.netto_weight >= product.weight) {
    return false;
  }

  return true;
};

/**
 * Hook to manage product boms and create/delete new product boms
 * @param productId - Product ID
 * @returns - BomComponent[] | null - List of bom components
 */
interface UseProductBoms {
  productBoms: BomComponent[] | [];
  loading: boolean;
  error: string | null;
  getAllProductBoms: (id: number) => Promise<void>;
  createProductBom: () => Promise<number | false>;
  deleteBomById: (bomId: number) => Promise<void>;
}
export const useProductBoms = (productId: number): UseProductBoms => {
  const accessToken = localStorage.getItem('access_token');
  const [productBoms, setProductBoms] = useState<BomComponent[] | []>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getAllProductBoms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}portal/products/${productId}/boms`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product bom');
      }

      const data: BomComponent[] = await response.json();
      setProductBoms(data);
    } catch (err) {
      setError('Failed to fetch product bom');
    } finally {
      setLoading(false);
    }
  }, []);

  const createProductBom = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}portal/products/${productId}/boms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error('Failed to create product bom');
      }

      getAllProductBoms();
      const res = (await response.json()) as BomComponent;
      return res.id;
    } catch (err) {
      setError('Failed to create product bom');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteBomById = async (bomId: number) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}portal/products/boms/${bomId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete product bom');
      }

      getAllProductBoms();
    } catch (err) {
      setError('Failed to delete product bom');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllProductBoms();
  }, [productId, getAllProductBoms]);

  return { productBoms, loading, error, getAllProductBoms, createProductBom, deleteBomById };
};

/**
 * Hook to manage product bom lines and create new product bom lines
 * @param bomId - Bom ID
 * @returns - BomLine[] | null - List of bom lines
 */
interface UseProductBomLines {
  bomLines: BomLine[] | null;
  loading: boolean;
  error: string | null;
  getAllBomLines: (id: number) => Promise<void>;
  createBomLine: (bomId: number, bomLine: BomLineIn) => Promise<void>;
}
export const useProductBomLines = (bomId?: number): UseProductBomLines => {
  const accessToken = localStorage.getItem('access_token');
  const [bomLines, setBomLines] = useState<BomLine[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getAllBomLines = useCallback(async (id: number) => {
    try {
      if (!bomId && !id) return;
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}portal/products/boms/${bomId || id}/lines`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bom lines');
      }

      const data: BomLine[] = await response.json();
      setBomLines(data);
    } catch (err) {
      setError('Failed to fetch bom lines');
    } finally {
      setLoading(false);
    }
  }, []);

  const createBomLine = async (bomId: number, bomLine: BomLineIn) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}portal/products/boms/${bomId}/lines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(bomLine),
      });

      if (!response.ok) {
        throw new Error('Failed to create bom line');
      }

      getAllBomLines(bomId);
    } catch (err) {
      setError('Failed to create bom line');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bomId) {
      getAllBomLines(bomId);
    }
  }, [bomId, getAllBomLines]);

  return { bomLines, loading, error, getAllBomLines, createBomLine };
};

/**
 * Hook to manage product bom line
 * @param lineId - Bom Line ID
 * @returns  - BomLine | null - Bom Line
 */
interface UseProductBomLine {
  bomLine: BomLine | null;
  loading: boolean;
  error: string | null;
  getBomLine: (id: number) => Promise<void>;
  updateBomLine: (id: number, bomLine: BomLineIn) => Promise<void>;
  deleteBomLine: (id: number) => Promise<void>;
}
export const useProductBomLine = (lineId?: number): UseProductBomLine => {
  const accessToken = localStorage.getItem('access_token');
  const [bomLine, setBomLine] = useState<BomLine | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getBomLine = useCallback(async (id: number) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}portal/products/boms/lines/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bom line');
      }

      const data: BomLine = await response.json();
      setBomLine(data);
    } catch (err) {
      setError('Failed to fetch bom line');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateBomLine = async (id: number, bomLine: BomLineIn) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}portal/products/boms/lines/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(bomLine),
      });

      if (!response.ok) {
        throw new Error('Failed to update bom line');
      }

      getBomLine(id);
    } catch (err) {
      setError('Failed to update bom line');
    } finally {
      setLoading(false);
    }
  };

  const deleteBomLine = async (id: number) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}portal/products/boms/lines/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete bom line');
      }

      setBomLine(null);
    } catch (err) {
      setError('Failed to delete bom line');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lineId) {
      getBomLine(lineId);
    }
  }, [lineId, getBomLine]);

  return { bomLine, loading, error, getBomLine, updateBomLine, deleteBomLine };
};
